/**
 * URL State Utilities: Critique View URL State Management
 * Feature: 040-critique-url-state
 *
 * Utilities for parsing, validating, and building URL query parameters
 * for critique view navigation.
 */

import { isValidGlobalId } from './urls';
import type { ParsedUrlParams, CritiqueUrlState } from '../types/critiqueUrlState';

/**
 * Parse and validate URL query parameters
 *
 * Extracts photo and comment IDs from URLSearchParams with format validation.
 * Does NOT validate existence in collection - caller's responsibility.
 *
 * @param searchParams - URLSearchParams from useSearchParams()
 * @returns Parsed and validated parameters with errors
 *
 * @example
 * ```tsx
 * const [searchParams] = useSearchParams();
 * const parsed = parseUrlParams(searchParams);
 *
 * if (parsed.photoId) {
 *   selectPhoto(parsed.photoId);
 * }
 * if (parsed.errors.invalidPhotoFormat) {
 *   console.warn('Invalid photo ID format in URL');
 * }
 * ```
 */
export function parseUrlParams(searchParams: URLSearchParams): ParsedUrlParams {
  const rawPhoto = searchParams.get('photo');
  const rawComment = searchParams.get('comment');

  // Validate format (not existence in collection)
  const photoId = rawPhoto && isValidGlobalId(rawPhoto) ? rawPhoto : null;
  const commentId = rawComment && isValidGlobalId(rawComment) ? rawComment : null;

  return {
    rawPhoto,
    rawComment,
    photoId,
    commentId,
    errors: {
      invalidPhotoFormat: !!rawPhoto && !photoId,
      invalidCommentFormat: !!rawComment && !commentId,
    },
  };
}

/**
 * Build URL query parameters with photo only
 *
 * Constructs query string for photo selection (removes comment parameter).
 *
 * @param photoId - Global ID of the photo
 * @returns Query parameter object { photo: string }
 *
 * @example
 * ```tsx
 * const params = buildPhotoUrl('ph5x');
 * setSearchParams(params); // URL becomes ?photo=ph5x
 * ```
 */
export function buildPhotoUrl(photoId: string): { photo: string } {
  return { photo: photoId };
}

/**
 * Build URL query parameters with photo and comment
 *
 * Constructs query string for photo + comment selection.
 *
 * @param photoId - Global ID of the photo
 * @param commentId - Global ID of the comment
 * @returns Query parameter object { photo: string; comment: string }
 *
 * @example
 * ```tsx
 * const params = buildPhotoCommentUrl('ph5x', 'cm9a');
 * setSearchParams(params); // URL becomes ?photo=ph5x&comment=cm9a
 * ```
 */
export function buildPhotoCommentUrl(
  photoId: string,
  commentId: string
): { photo: string; comment: string } {
  return { photo: photoId, comment: commentId };
}

/**
 * Validate photo exists in collection
 *
 * Checks if photo ID exists in the loaded collection photos.
 *
 * @param photoId - Global ID to validate
 * @param photos - Array of photos in the collection (PhotoItem2ViewModel format with photo wrapper)
 * @returns True if photo exists, false otherwise
 *
 * @example
 * ```tsx
 * const isValid = validatePhotoInCollection('ph5x', photos);
 * if (!isValid) {
 *   setError({ photoNotFound: true });
 * }
 * ```
 */
export function validatePhotoInCollection(
  photoId: string,
  photos: Array<{ id?: string; photoData?: { id: string }; photo?: { id: string } }> | undefined
): boolean {
  if (!photos) return false;
  // Support both PhotoItem2Data format (photo.photoData.id) and legacy format (photo.id)
  // Filter out undefined entries before checking
  return photos.some((p) => p && (p.photoData?.id || p.photo?.id || p.id) === photoId);
}

/**
 * Create CritiqueUrlState from parsed parameters
 *
 * Constructs complete URL state with existence validation.
 *
 * @param parsed - Parsed URL parameters from parseUrlParams()
 * @param photos - Array of photos for existence validation
 * @returns Complete URL state with errors
 *
 * @example
 * ```tsx
 * const parsed = parseUrlParams(searchParams);
 * const state = createUrlState(parsed, photos);
 * // state.photoId is null if invalid or not found
 * // state.errors contains all validation failures
 * ```
 */
export function createUrlState(
  parsed: ParsedUrlParams,
  photos: Array<{ id?: string; photoData?: { id: string }; photo?: { id: string } }> | undefined
): CritiqueUrlState {
  const { photoId, commentId, errors } = parsed;

  // Check if photo exists in collection
  const photoExists = photoId ? validatePhotoInCollection(photoId, photos) : false;

  return {
    photoId: photoExists ? photoId : null,
    commentId: photoExists ? commentId : null, // Only keep comment if photo is valid
    errors: {
      ...errors,
      photoNotFound: !!photoId && !photoExists,
    },
  };
}
